import {
  DealersByRegion,
  PPNDealer,
  PPNDealerSearchResult,
  PPNDistance,
} from "../entities/dealer-response";
import {Environment} from "../entities/environment";
import {LatLong} from "../entities/places";
import {NaviError, NaviErrorTypes} from "../utility/navi-error";
import {navStateStore} from "../state/nav-state-store";
import {getConfig} from "../utility/helper";
import {Statuscodes} from "../utility/constants/statuscodes";
import {getUsedUnit} from "../utility/helper/dealer-search-helper";

const cachedDealers = new Map<string, PPNDealerSearchResult[]>();

export async function getAllPPNDealersForMarket(
  env: Environment,
  dealerApiUrl: string,
  country: string,
): Promise<PPNDealerSearchResult[]> {
  const cacheKey = `${env}-${country}`;
  if (cachedDealers.has(cacheKey)) {
    return cachedDealers.get(cacheKey)!;
  }
  return getPPNDealers(
    env,
    dealerApiUrl,
    {latitude: 0, longitude: 0},
    country,
    {radius: 10000000},
  ).then((dealers) => {
    // Distance is not shown since we're sending 0,0 coordinates and big radius to get all dealers
    const results = dealers.map((dealer) => {
      return {
        ...dealer,
        distance: undefined,
      };
    });
    cachedDealers.set(cacheKey, results);
    return results;
  });
}

interface GetPPNDealersOptions {
  /**
   * The radius around the given coordinates to search for dealers
   */
  radius?: number;
}

function convertCountryCode(country: string): string {
  const mappedCountries = new Map([
    ["AE-AZ", "XA"],
    ["AE-DU", "XD"],
  ]);

  return mappedCountries.get(country) ?? country;
}

export async function getPPNDealers(
  _env: Environment,
  dealerApiUrl: string,
  coordinates: LatLong,
  country: string,
  options: GetPPNDealersOptions = {},
): Promise<PPNDealerSearchResult[]> {
  let response: Response;
  let ppnDealerSearchResults: { dealer: PPNDealer; distance: PPNDistance }[];
  country = convertCountryCode(country);

  try {
    const headers = new Headers();
    headers.set("Accept", "*/*");

    const useMiles = getUsedUnit(country) === "mi";
    const requestParams = new URLSearchParams({
      coordinates: `${coordinates.latitude},${coordinates.longitude}`,
      unit: useMiles ? "MI" : "KM",
      radius: useMiles ? "31" : "50", //  kilometers * 0.621371
    });

    if (options?.radius) {
      requestParams.set("radius", options.radius.toString());
    }

    response = await fetch(
      `${dealerApiUrl}/dealers/${country}?${requestParams.toString()}`,
      {
        method: "GET",
        headers,
      },
    );
  } catch (err) {
    throw new NaviError("Unable to fetch dealers", NaviErrorTypes.GENERAL);
  }

  if (response.status === Statuscodes.badRequest) {
    throw new NaviError(
      "dealerService.getPPNDealers failed with invalid coordinates",
      NaviErrorTypes.GENERAL,
    );
  }

  if (response.status === Statuscodes.notFound) {
    throw new NaviError(
      "dealerService.getPPNDealers failed with invalid country code and therefore responded with no results",
      NaviErrorTypes.NO_RESULTS,
    );
  }

  if (response.status === Statuscodes.internalServerError) {
    throw new NaviError(
      "dealerService.getPPNDealers failed with invalid response",
      NaviErrorTypes.GENERAL,
    );
  }

  try {
    ppnDealerSearchResults = await response.json();
  } catch (err) {
    throw new NaviError(
      "dealerService.getPPNDealers could not parse response into json format",
      NaviErrorTypes.GENERAL,
    );
  }

  return ppnDealerSearchResults.map(({dealer, ...rest}) => {
    return {ppnDealer: dealer, ...rest};
  });
}

export async function getPPNDealer(
  env: Environment,
  dealerApiUrl: string,
  id: string,
  coordinates: LatLong,
  country: string,
): Promise<PPNDealerSearchResult> {
  // (LR/MX) 13.09.2022: If we ever remove the workaround, remember to map the dealer type to ppnDealer
  let ppnDealer: PPNDealerSearchResult | undefined;

  try {
    const ppnDealerSearchResults = await getPPNDealers(
      env,
      dealerApiUrl,
      coordinates,
      country,
    );
    ppnDealer = ppnDealerSearchResults.find(
      (dealerSearchResult) => id === dealerSearchResult.ppnDealer.id,
    );
  } catch (err) {
    throw new NaviError("Unable to fetch a dealer", NaviErrorTypes.GENERAL);
  }

  if (ppnDealer === undefined) {
    throw new NaviError(
      `Requested dealer ${id} could not be found`,
      NaviErrorTypes.NO_RESULTS,
    );
  }

  return ppnDealer;
}

const dealersByRegionMap = new Map<string, DealersByRegion | undefined>();

export async function getPPNDealerByRegion(
  id: string,
  region: string,
): Promise<PPNDealerSearchResult> {
  const dealersOfRegion = await getPPNDealersByRegion(region);

  const dealerResult = dealersOfRegion.regions
    .flatMap(({cities}) =>
      cities.flatMap(({dealers}) =>
        Object.values(dealers).reduce<{ ppnDealer: PPNDealer }[]>(
          (dealerList, dealer) => [...dealerList, dealer],
          [],
        ),
      ),
    )
    .find(({ppnDealer}) => ppnDealer.id === id);

  if (dealerResult === undefined) {
    throw new NaviError(
      `Requested dealer could not be found`,
      NaviErrorTypes.NO_RESULTS,
    );
  }

  return dealerResult;
}

export async function getPPNDealersByRegion(
  region: string,
): Promise<DealersByRegion> {
  const {env} = navStateStore.state;
  const headers = new Headers();
  let dealersByRegion: DealersByRegion | undefined;

  if (dealersByRegionMap.has(region)) {
    dealersByRegion = dealersByRegionMap.get(region);
  } else {
    const {BFF_ENDPOINT} = getConfig(env);

    headers.set("Accept", "*/*");

    const response = await fetch(
      `${BFF_ENDPOINT}/dealers/region/${region}?env=${env}`,
      {
        method: "GET",
        headers,
      },
    );

    dealersByRegion = await response.json();
    dealersByRegionMap.set(region, dealersByRegion);
  }

  if (dealersByRegion === undefined) {
    throw new NaviError(
      `Requested dealer could not be found`,
      NaviErrorTypes.NO_RESULTS,
    );
  }

  return dealersByRegion;
}
